import React, { MouseEvent, useState, FC } from "react";

import { QueryObserverResult } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  useBoolean,
  useClipboard,
  useApisMembersEmployeeSurveySubmissionsEmployeeSurveySubmissionRepliesDestroy,
  useApisMembersEmployeeSurveySubmissionsEmployeeSurveySubmissionRepliesUpdate,
  useEditorState,
  useEditorStateLinkContentGetter,
} from "~/hooks";

import {
  EmployeeSurveySubmissionReplyDetail,
  CommentEditForm,
} from "~/components/organisms";

import {
  EmployeeSurveySubmissionReplyType,
  PostDetailMenuItemType,
  AvatarAndNameEmployeeType,
  PreviewWithFileType,
  ApiMemberEmployeeSurveySubmissionRepliesIndexResponseType,
} from "~/domains";

import { Href } from "~/constants/postUrl";

type PropsType = {
  isHighlight?: boolean;
  employeeSurveySubmissionReply: EmployeeSurveySubmissionReplyType;
  mentions: AvatarAndNameEmployeeType[];
  refetch: () => Promise<
    QueryObserverResult<
      ApiMemberEmployeeSurveySubmissionRepliesIndexResponseType,
      unknown
    >
  >;
};

export const EmployeeSurveySubmissionReplyDetailWithEditForm: FC<PropsType> = ({
  isHighlight = false,
  employeeSurveySubmissionReply,
  refetch,
  mentions,
}: PropsType) => {
  const { clipboardToCopy } = useClipboard();
  const {
    isChecked: isEditFormOpen,
    setFalse: closeEditForm,
    toggle: toggleEditForm,
  } = useBoolean(false);

  const [editorState] = useEditorState(employeeSurveySubmissionReply.content);
  const [files, setFiles] = useState<PreviewWithFileType[]>([]);
  const { fetchedData } = useEditorStateLinkContentGetter({
    watchEditorState: editorState.value,
    isWatch: isEditFormOpen,
  });

  const { mutate: updateRequest, isLoading } =
    useApisMembersEmployeeSurveySubmissionsEmployeeSurveySubmissionRepliesUpdate();

  const { mutate: deleteRequest } =
    useApisMembersEmployeeSurveySubmissionsEmployeeSurveySubmissionRepliesDestroy();

  const deleteEmployeeSurveySubmissionReplyRequest = () => {
    const isConfirm = confirm("本当に削除しますか？");
    if (!isConfirm) return;

    deleteRequest(
      {
        employeeSurveySubmissionId:
          employeeSurveySubmissionReply.employeeSurveySubmissionId,
        id: employeeSurveySubmissionReply.id,
      },
      {
        onSuccess: async (data) => {
          toast(data.message);
          await refetch();
        },
      },
    );
  };

  const handleSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const body = {
      content: editorState.jsonContent,
      attachFiles: files,
      quotePostId: fetchedData?.id,
    };
    updateRequest(
      {
        employeeSurveySubmissionId:
          employeeSurveySubmissionReply.employeeSurveySubmissionId,
        id: employeeSurveySubmissionReply.id,
        body,
      },
      {
        onSuccess: async (data) => {
          toast(data.message);
          await refetch().then(() => {
            closeEditForm();
          });
        },
      },
    );
  };

  const detailItems: PostDetailMenuItemType[] = [
    {
      icon: "ioCopyOutline",
      text: "リンクをコピー",
      onClick: () =>
        clipboardToCopy(
          `${location.origin}${Href.EmployeeSurveySubmissionReply(employeeSurveySubmissionReply.id)}`,
        ),
      disabled: false,
    },
    {
      icon: "ioCreateOutline",
      text: "編集する",
      onClick: () => toggleEditForm(),
      disabled: !employeeSurveySubmissionReply.editable,
    },
    {
      icon: "ioTrashOutline",
      text: "削除する",
      onClick: () => deleteEmployeeSurveySubmissionReplyRequest(),
      disabled: !employeeSurveySubmissionReply.editable,
    },
  ];

  return (
    <div className="" id={employeeSurveySubmissionReply.id}>
      {isEditFormOpen ? (
        <CommentEditForm
          submitButton={{
            isSubmitting: isLoading,
            onSubmit: handleSubmit,
          }}
          mentions={mentions}
          post={{
            createdAt: employeeSurveySubmissionReply.createdAt,
            employee: employeeSurveySubmissionReply.employee,
            content: employeeSurveySubmissionReply.content,
          }}
          closeEditForm={closeEditForm}
          files={files}
          setFiles={setFiles}
          editorStateContent={editorState.value}
          setEditorStateContent={editorState.onChange}
          blockQuote={fetchedData}
        />
      ) : (
        <EmployeeSurveySubmissionReplyDetail
          employeeSurveySubmissionReply={employeeSurveySubmissionReply}
          menuItems={detailItems}
          isHighlight={isHighlight}
        />
      )}
    </div>
  );
};
